import { useEffect, useState } from 'react';

/** Distance from the viewport top, in px, at which a heading counts as the current section. */
const DEFAULT_OFFSET = 120;

/**
 * Scroll-spy for the article's table of contents. The active id is the last heading whose top has crossed
 * `offset`, so it stays right while scrolling either way. Headings are looked up by id on every frame.
 */
export default function useActiveHeading(ids: string[], offset = DEFAULT_OFFSET) {
  const [activeId, setActiveId] = useState<string | null>(ids[0] ?? null);

  useEffect(() => {
    if (!ids.length) return undefined;

    let frame = 0;

    const update = () => {
      frame = 0;
      const passed = ids.filter((id) => {
        const heading = document.getElementById(id);
        return !!heading && heading.getBoundingClientRect().top <= offset;
      });
      setActiveId(passed[passed.length - 1] ?? ids[0]);
    };

    const scheduleUpdate = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
    };
  }, [ids, offset]);

  return { activeId, setActiveId };
}
